import { useState, useCallback, useEffect } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { setSpaceKey as setSpaceKeyInCache, getSpaceKey } from '../constants.tsx';

export const useGetSpaceKey = (projectName: string, coUseEligible: boolean): string => {
	const [spaceKey, setSpaceKey] = useState('');

	const checkForConfluenceSpace = useCallback(async () => {
		if (coUseEligible && projectName) {
			try {
				if (fg('whiteboards-cql-fix-get-space-key')) {
					const cachedKey = getSpaceKey();
					if (cachedKey) {
						setSpaceKey(cachedKey);
						return;
					}

					const cql = `type = space and title = "${projectName}"`;
					const api = `/wiki/rest/api/search?cql=${encodeURIComponent(cql)}`;
					const res = await performGetRequest(api);

					const key = res?.results?.[0]?.space?.key;
					if (typeof key === 'string') {
						setSpaceKeyInCache(key);
						setSpaceKey(key);
					}
				} else {
					const cql = `type = space and title = "${projectName}"`;
					const api = `/wiki/rest/api/search?cql=${cql}`;
					const res = await performGetRequest(api);
					if (res.results?.length) {
						setSpaceKey(res.results[0].space.key);
					}
				}
			} catch (error) {
				if (error instanceof FetchError) {
					if (error.statusCode === 403) {
						return;
					}

					fireErrorAnalytics({
						meta: {
							id: 'GetSpaceKey',
							packageName: 'jiraSoftwareBacklog',
						},
						attributes: {
							traceId: error.traceId,
							statusCode: error.statusCode,
						},
						error: new Error(
							`Fetch Error when searching for a space key with a given project name, status code: ${error.statusCode}`,
						),
					});
				} else {
					fireErrorAnalytics({
						meta: {
							id: 'GetSpaceKey',
							packageName: 'jiraSoftwareBacklog',
						},
						error: new Error(
							'Unknown error when searching for a space key with a given project name',
						),
					});
				}
				setSpaceKey('');
			}
		}
	}, [coUseEligible, projectName]);

	useEffect(() => {
		checkForConfluenceSpace();
	}, [checkForConfluenceSpace]);

	return spaceKey;
};
